import { useEffect, useMemo, useRef } from "react";

import { X, Clock, ListChecks, Activity } from "lucide-react";

import { AreaChartCard, LineChartCard } from "../../components/charts/Charts";
import PortalIE from "../../components/ui/PortalIE";
import {
  Card,
  CardTitle,
  Chip,
  Skeleton,
  EstadoVazio
} from "../../components/ui/primitives";
import { api } from "../../lib/api";
import {
  toNumber,
  formatPeriodo,
  fmtTempo,
  initials,
  avatarColor
} from "../../lib/format";
import { classificarPerfil } from "../../lib/perfilEngajamento";
import { useAsync } from "../../lib/useAsync";

/* Geometria do drawer em estilo INLINE, não em classes utilitárias.

   Motivo: estas são as propriedades das quais o drawer depende para funcionar —
   se qualquer uma falhar, ele quebra de formas confusas (não cobre a tela, não
   rola, o clique atravessa para a lista). Elas ficariam sujeitas a três coisas
   que já deram problema aqui: colisão de nome com utilitárias do Bootstrap,
   ordem da cascata, e o CSS gerado estar desatualizado em relação ao JSX.
   Estilo inline não depende de nada disso.

   O visual (cores, sombra, borda, tipografia) continua em classes Tailwind —
   ali uma divergência é cosmética, não funcional.

   z-index: acima do sidebar (1032), header (1029) e footer (1030) do AVA, e
   abaixo do .modal-backdrop do Bootstrap (1040), para que um modal da
   plataforma continue aparecendo por cima do drawer. */
const ESTILO_OVERLAY = {
  position: "fixed",
  inset: 0,
  zIndex: 1034,
  background: "rgba(0, 0, 0, 0.55)"
};

const ESTILO_PAINEL = {
  position: "fixed",
  top: 0,
  bottom: 0,
  right: 0,
  zIndex: 1035,
  display: "flex",
  flexDirection: "column",
  width: "100%",
  maxWidth: "min(560px, 92vw)"
};

/* `minHeight: 0` é o que faz a rolagem funcionar.

   Um flex item em coluna tem `min-height: auto` por padrão, o que impede o
   encolhimento: o conteúdo transborda o painel em vez de rolar dentro dele. O
   `flex-1` do Tailwind define `flex: 1 1 0%`, mas não zera o min-height — por
   isso o drawer "não rolava para baixo". */
const ESTILO_CORPO = {
  flex: "1 1 0%",
  minHeight: 0,
  overflowY: "auto"
};

export function AlunoDrawer({ aluno, turmaId, ano, range, onClose }) {
  const { usuario_id: usuarioId, nome } = aluno;
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [usuarioId]);

  // Fecha com Esc e trava a rolagem do fundo enquanto o drawer está aberto —
  // sem isso a página de trás rola junto e o drawer perde a própria rolagem.
  useEffect(() => {
    const aoTeclar = e => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", aoTeclar);
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", aoTeclar);
      document.body.style.overflow = overflowAnterior;
    };
  }, [onClose]);

  /* Renderizado por PortalIE (ver o arquivo para o porquê): sem o portal, o
     `position: fixed` se ancorava no Card — que tem backdrop-filter e transform
     — em vez do viewport, e o drawer não cobria a tela, não rolava, e o clique
     atravessava para a linha da tabela por baixo. */
  return (
    <PortalIE>
      <div style={ESTILO_OVERLAY} onClick={onClose} aria-hidden />

      <aside
        className="border-l border-[var(--color-border)] bg-[var(--color-surface)] shadow-2xl shadow-black/10"
        style={ESTILO_PAINEL}
        role="dialog"
        aria-modal="true"
        aria-label={`Detalhes de ${nome}`}
      >
        <header className="relative flex items-center gap-3 border-b border-[var(--color-border)] px-5 py-4 pr-14">
          <div
            className="grid h-10 w-10 flex-none place-items-center rounded-full text-[13px] font-semibold text-white"
            style={{ background: avatarColor(usuarioId) }}
          >
            {initials(nome)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[15px] font-semibold text-[var(--color-ink)]">
              {nome}
            </div>
            <div className="text-[12px] text-[var(--color-ink-faint)]">
              Execução de atividades
            </div>
          </div>
          {/* Fechar ancorado no canto superior direito do drawer, redondo e
              discreto — o quadrado com borda forte competia com o avatar. */}
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full text-[var(--color-ink-faint)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </header>

        <div ref={scrollRef} className="p-5" style={ESTILO_CORPO}>
          <AlunoPainelContent
            aluno={aluno}
            turmaId={turmaId}
            ano={ano}
            range={range}
          />
        </div>
      </aside>
    </PortalIE>
  );
}

/** Miolo do painel de aluno (perfil de engajamento, tempo online, execução,
 * score) — sem chrome de drawer, pra poder ser usado tanto dentro do
 * AlunoDrawer (Admin/Professor) quanto solto numa página (explorer do Gestor). */
export function AlunoPainelContent({ aluno, turmaId, ano, range }) {
  const { usuario_id: usuarioId } = aluno;
  return (
    <div className="space-y-5">
      <PerfilEngajamentoCard aluno={aluno} />
      <TempoOnlineCard usuarioId={usuarioId} ano={ano} range={range} />
      <ExecucaoAlunoCard
        usuarioId={usuarioId}
        turmaId={turmaId}
        ano={ano}
        range={range}
      />
      <EngajamentoAlunoCard
        usuarioId={usuarioId}
        turmaId={turmaId}
        ano={ano}
        range={range}
      />
    </div>
  );
}

const PILARES = [
  ["Progresso", "eng_progresso"],
  ["Qualidade do tempo", "eng_qualidade"],
  ["Pontualidade", "eng_pontualidade"],
  ["Exploração livre", "eng_exploracao"]
];

const TONE_COLOR = {
  good: "var(--color-good)",
  warn: "var(--color-warn)",
  bad: "var(--color-bad)",
  none: "var(--color-ink-faint)"
};

function PerfilEngajamentoCard({ aluno }) {
  const perfil = useMemo(() => classificarPerfil(aluno), [aluno]);
  const score = aluno.score_engajamento;

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <CardTitle meta="Leitura automática dos 4 pilares">
          Perfil de engajamento
        </CardTitle>
        <div className="text-right">
          <div
            className="tnum text-[26px] font-bold leading-none"
            style={{ color: TONE_COLOR[perfil.tone] }}
          >
            {score == null ? "–" : `${Math.round(score)}%`}
          </div>
          <div className="mt-1 text-[10.5px] uppercase tracking-[0.06em] text-[var(--color-ink-faint)]">
            score final
          </div>
        </div>
      </div>

      <div className="mb-4 flex justify-center">
        <Chip tone={perfil.tone} size="md">
          <perfil.icon className="h-3.5 w-3.5" aria-hidden />
          {perfil.titulo}
        </Chip>
      </div>
      <p className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3.5 text-[13px] leading-relaxed text-[var(--color-ink-soft)]">
        {perfil.resumo}
      </p>

      <div className="mt-4 grid gap-2.5">
        {PILARES.map(([label, key]) => {
          const pct = Math.max(0, Math.min(100, Number(aluno[key]) || 0));
          return (
            <div
              key={key}
              className="grid grid-cols-[140px_1fr_44px] items-center gap-3"
            >
              <span className="text-[12px] text-[var(--color-ink-soft)]">
                {label}
              </span>
              <span className="block h-1.5 overflow-hidden rounded-full bg-[var(--color-surface-3)]">
                <span
                  className="block h-full rounded-full bg-[var(--color-data)] transition-[width] duration-700"
                  style={{ width: `${pct}%` }}
                />
              </span>
              <span className="tnum text-right text-[12px] font-semibold text-[var(--color-ink)]">
                {Math.round(pct)}%
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function TempoOnlineCard({ usuarioId, ano, range }) {
  const gran = range.gran;
  const t = useAsync(
    () => api.alunoTempoOnline(usuarioId, gran, ano, range.inicio, range.fim),
    [usuarioId, gran, ano, range.inicio, range.fim]
  );

  const serie = useMemo(
    () =>
      (t.data?.serie ?? []).map(r => ({
        periodo_fmt: formatPeriodo(r.periodo, gran),
        minutos: Math.round((r.tempo_seg ?? 0) / 60)
      })),
    [t.data, gran]
  );

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <CardTitle meta="Sessões reais de interação (logs)">
          Tempo online
        </CardTitle>
        {!t.loading && (
          <div className="text-right">
            <div className="tnum text-[22px] font-bold leading-none text-[var(--color-ink)]">
              {fmtTempo(t.data?.total_seg ?? 0)}
            </div>
            <div className="mt-1 text-[10.5px] uppercase tracking-[0.06em] text-[var(--color-ink-faint)]">
              em {t.data?.dias_online ?? 0}{" "}
              {t.data?.dias_online === 1 ? "dia" : "dias"}
            </div>
          </div>
        )}
      </div>
      {t.loading ? (
        <Skeleton className="h-[200px] w-full" />
      ) : serie.length ? (
        <AreaChartCard
          data={serie}
          xKey="periodo_fmt"
          series={[
            { key: "minutos", label: "Minutos", color: "var(--color-accent)" }
          ]}
          height={200}
          xTickAngle={-25}
          xInterval={gran === "dia" ? "preserveStartEnd" : 0}
        />
      ) : (
        <EstadoVazio
          icon={<Clock size={20} />}
          titulo="Sem tempo online no período"
          hint="Nenhuma sessão registrada nos logs de acesso."
        />
      )}
    </Card>
  );
}

function ExecucaoAlunoCard({ usuarioId, turmaId, ano, range }) {
  const gran = range.gran;
  const exec = useAsync(
    () =>
      api.alunoExecucao(usuarioId, gran, turmaId, ano, range.inicio, range.fim),
    [usuarioId, gran, turmaId, ano, range.inicio, range.fim]
  );

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <CardTitle meta="Iniciadas vs. concluídas">
          Execução de atividades
        </CardTitle>
      </div>
      {exec.loading ? (
        <Skeleton className="h-[240px] w-full" />
      ) : exec.data?.serie.length ? (
        <LineChartCard
          data={exec.data.serie.map(r => ({
            ...r,
            periodo_fmt: formatPeriodo(r.periodo, gran)
          }))}
          xKey="periodo_fmt"
          series={[
            {
              key: "iniciadas",
              label: "Iniciadas",
              color: "var(--color-chart-1)"
            },
            {
              key: "concluidas",
              label: "Concluídas",
              color: "var(--color-chart-3)"
            }
          ]}
          height={240}
        />
      ) : (
        <EstadoVazio
          icon={<ListChecks size={20} />}
          titulo="Sem execução no período"
          hint="Nenhuma atividade iniciada ou concluída na janela selecionada."
        />
      )}
    </Card>
  );
}

function EngajamentoAlunoCard({ usuarioId, turmaId, ano, range }) {
  const gran = range.gran;
  const eng = useAsync(
    () => api.turmaEngajamento(turmaId, gran, ano, range.inicio, range.fim),
    [turmaId, gran, ano, range.inicio, range.fim]
  );

  const serie = useMemo(() => {
    const pts = (eng.data ?? []).filter(p => p.usuario_id === usuarioId);
    return pts
      .map(p => ({ periodo: p.periodo, score: toNumber(p.score) }))
      .filter(p => p.score !== null)
      .sort((a, b) => a.periodo.localeCompare(b.periodo))
      .map(p => ({
        periodo_fmt: formatPeriodo(p.periodo, gran),
        score: Math.round(p.score)
      }));
  }, [eng.data, usuarioId, gran]);

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <CardTitle meta="Score final ao longo do tempo">
          Score de engajamento
        </CardTitle>
      </div>
      {eng.loading ? (
        <Skeleton className="h-[200px] w-full" />
      ) : serie.length ? (
        <AreaChartCard
          data={serie}
          xKey="periodo_fmt"
          series={[
            { key: "score", label: "Score final", color: "var(--color-data)" }
          ]}
          height={200}
          yDomain={[0, 100]}
          yTickFormatter={v => `${v}%`}
          xTickAngle={-25}
          xInterval={gran === "dia" ? "preserveStartEnd" : 0}
        />
      ) : (
        <EstadoVazio
          icon={<Activity size={20} />}
          titulo="Sem dados de engajamento"
          hint="Nenhum score registrado para o aluno no período."
        />
      )}
    </Card>
  );
}
